import { boardAspect } from "@/model/Settings";
import type { ZoomedRectangle } from "@/model/board";
import type {
  BoardCoordinate,
  RelativeCoordinate,
  SizedRectangle,
  ViewportCoordinate,
  WindowCoordinate,
  WindowLen,
} from "@/model/coordinates";
import { windowLen } from "@/model/coordinates";
import { useBoardSizeStore } from "@/store/boardSize";
import { useSidePanelStore } from "@/store/sidePanel";
import { useZoomStore } from "@/store/zoom";
import {
  clientCoord,
  clientSize,
  contentSize,
  scrollPos,
  scrollSize,
  windowFromViewport,
} from "@/utils/dom/coordinates";

import { boardScaleFromWindow, relativeFromWindow, scrollDiff } from "./coordinate-transform";
import { minCoord, minus, plus, times } from "./coordinates";

/**
 * If the card is outside the board/viewport, moves it back inside the board.
 * This modifies the first input parameter (pos) in place.
 * If 3rd arg (checkViewport) is true, the card is also moved back inside the viewport
 *
 * @returns The offset by which the card was moved
 */
export function validateCardPositionInBoard(
  pos: BoardCoordinate,
  size: BoardCoordinate,
  checkViewport: boolean,
): BoardCoordinate {
  const original = { ...pos };
  const boardRect = useBoardSizeStore().board;
  const zoom = useZoomStore().factor;

  // card inside board
  const boardSize = boardScaleFromWindow(boardRect.size);
  const overlap = minus(plus(pos, size), boardSize);
  if (pos.x < 0) {
    pos.x = 0;
  }
  if (overlap.x > 0) {
    pos.x -= overlap.x;
  }
  if (pos.y < 0) {
    pos.y = 0;
  }
  if (overlap.y > 0) {
    pos.y -= overlap.y;
  }

  if (!checkViewport) {
    return minus(pos, original);
  }

  const outline = 2; // the outline of the sticky note
  const gap = 2; // desired gap between sticky note and viewport

  // Adjust viewport size for any open side panels
  const leftPanelSize = useSidePanelStore().left.width;
  const rightPanelSize = useSidePanelStore().right.width;
  const scroll = scrollPos();
  const content = contentSize();

  // card inside view port (and not hidden by side panels)
  const viewportLeftTop = windowLen(
    scroll.x - boardRect.p0.x + leftPanelSize + outline + gap,
    scroll.y + outline + gap,
  );
  const viewportRightBottom = windowLen(
    viewportLeftTop.x + content.x - leftPanelSize - rightPanelSize - outline * 3 - gap,
    scroll.y - boardRect.p0.y - outline - gap + content.y,
  );

  const overlapLeftTop = minus(times(pos, zoom), boardScaleFromWindow(viewportLeftTop));
  const overlapRightBottom = minus(
    times(plus(pos, size), zoom),
    boardScaleFromWindow(viewportRightBottom),
  );
  if (overlapLeftTop.x < 0) {
    pos.x -= overlapLeftTop.x / zoom;
  }
  if (overlapRightBottom.x > 0) {
    pos.x -= overlapRightBottom.x / zoom;
  }

  if (overlapLeftTop.y < 0) {
    pos.y -= overlapLeftTop.y / zoom;
  }
  if (overlapRightBottom.y > 0) {
    pos.y -= overlapRightBottom.y / zoom;
  }

  return minus(pos, original);
}

export function calcBoardRect(zoom = useZoomStore().factor): ZoomedRectangle {
  const margin = useBoardSizeStore().leftTopMargin;
  const windowSize = minus(visibleWindowSize(), useBoardSizeStore().totalVisibleMargin);
  const boardSpace = minus(windowSize, useBoardSizeStore().totalScrollableMargin);
  if (windowSize.x / windowSize.y < boardAspect) {
    // window is higher than boardAspect -> make board less high, add margin at top
    const size = windowLen(windowSize.x, windowSize.x / boardAspect);
    const shortened = windowLen(0, Math.max(0, (boardSpace.y - size.y * zoom) / 2));
    return { zoom, p0: plus(shortened, margin), size };
  }
  // window is broader than boardAspect -> make board less broad, add margin at left
  const size = windowLen(windowSize.y * boardAspect, windowSize.y);
  const shortened = windowLen(Math.max(0, (boardSpace.x - size.x * zoom) / 2), 0);
  return { zoom, p0: plus(shortened, margin), size };
}

/**
 * Adjust useBoardSizeStore().scrollableMargin so that
 * there is enough space for a side panel of given size without the board scrolling
 */
export function adjustBoardLeftSize(left: number) {
  const neededLeftSize = left - useBoardSizeStore().board.p0.x;
  if (neededLeftSize > 0) {
    const size = useBoardSizeStore().board;
    assureBoardLeftSize(left, size, size, 1);
  } else {
    removeBoardScrollMargin(windowLen(-neededLeftSize, 0));
  }
}

export function removeBoardScrollMargin(size?: WindowLen) {
  const docElem = document.documentElement;
  const maxScroll = minus(scrollSize(docElem), clientSize(docElem));
  const possibleLeftTop = minCoord([
    useBoardSizeStore().scrollableMargin.p0,
    scrollPos(),
    size || windowLen(Infinity, Infinity),
  ]);
  const possibleRightBottom = minCoord([
    useBoardSizeStore().scrollableMargin.p1,
    minus(maxScroll, scrollPos()),
  ]);
  if (
    possibleLeftTop.x !== 0 ||
    possibleLeftTop.y !== 0 ||
    possibleRightBottom.x !== 0 ||
    possibleRightBottom.y !== 0
  ) {
    useBoardSizeStore().addScrollableLeftTop(minus(possibleLeftTop));
    useBoardSizeStore().addScrollableRightBottom(minus(possibleRightBottom));
  }
}

function assureBoardLeftSize(
  size: number,
  initial: SizedRectangle,
  current: SizedRectangle,
  iter: number,
) {
  // endless loops should not happen, but better make sure
  if (iter > 3) {
    return;
  }
  const addLeft = Math.ceil(size - current.p0.x);
  if (addLeft >= 1) {
    useBoardSizeStore().addScrollableLeftTop(windowLen(addLeft, 0));
    if (maxScrollX(current) === 0) {
      // no scroll bars -> window wider than content -> board is centered -> add same space also to the right
      useBoardSizeStore().addScrollableRightBottom(windowLen(addLeft, 0));
    }
    return assureBoardLeftSize(size, initial, calcBoardRect(), iter + 1);
  }
  // assure there's enough space to the right so that we can scroll the board
  // the amount we added to the left (so it does not move visibly)
  const needScroll = plus(scrollPos(), minus(current.p0, initial.p0)).x;
  const addRight = Math.floor(needScroll) - Math.ceil(maxScrollX(current));
  if (addRight > 0) {
    useBoardSizeStore().addScrollableRightBottom(windowLen(addRight, 0));
  }
}

function maxScrollX(rect: SizedRectangle) {
  const contentWidth =
    rect.p0.x + rect.size.x * useZoomStore().factor + useBoardSizeStore().rightBottomMargin.x;
  return Math.max(0, contentWidth - document.documentElement.clientWidth);
}

export function relativeFromViewport(c: ViewportCoordinate): RelativeCoordinate {
  return relativeFromWindow(windowFromViewport(c));
}

export function relativeClientCoord(elem: MouseEvent): RelativeCoordinate {
  return relativeFromViewport(clientCoord(elem));
}

export function visibleWindowSize() {
  return minus(contentSize(), useBoardSizeStore().totalWindowMargin);
}

// scroll the window so that the window and the viewport coordinates are equal
export function scrollTo(w: WindowCoordinate, v: ViewportCoordinate) {
  const s = scrollDiff(w, v);
  window.scrollTo(s.x, s.y);
}

const offScreen = -10;

export function offScreenCoord<T extends ViewportCoordinate | RelativeCoordinate>() {
  return { x: offScreen, y: offScreen } as T;
}

export function isOffScreen(c: ViewportCoordinate | RelativeCoordinate) {
  return c.x === offScreen && c.y === offScreen;
}
